/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */
import React from 'react';
import useStyles from 'isomorphic-style-loader/useStyles';
import {
    func, object, number, shape, arrayOf, string,
} from 'prop-types';
import { uid } from 'react-uid';
import { useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import stickyProgressBar from '../../UiHelper/StickyProgressBar';
import BYOGroupSlider from './BYOGroupSlider';
import style from './BYOProductItems.module.css';
import byoTheme from './BYOTheme';
import { getBrand, getFeatureFlags  } from '../../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import ScrollbarContainer from '../../../../../GraphqlCategoryPage/Partials/ScrollbarContainer/ScrollbarContainer';
import useSSRMediaQuery from '../../../../../../../helpers/hooks/useSSRMediaQuery';
import ProgressBar from './ProgressBar';

const BYOProductItems = ({
    totalProductSelected,
    maxSelectableForSku,
    cyoData,
    handleItemSelection,
    selectedItems,
    removeItem,
    error,
    handleRemoveError,
    maxSelectableItem,
    stepIndex,
}) => {
    useStyles(style);
    const brandData = useSelector(getBrand);
    const { progressBarRef, isStickyHeader } = stickyProgressBar();
    const brandCode = brandData?.code;
    const device = useSSRMediaQuery({
        desktop: 1024, // this layout is visible till 1024
    });
    // default color assigned when no brand code found
    const {
        buttonColorClear = '#512C1C',
    } = byoTheme[brandCode] || {};
    const cyoNonCountableData = cyoData.cyoGroup.filter((g) => g.type !== 'cyoNonCountable');
    const visibleGroups = cyoNonCountableData.filter((item) => item.type !== 'cyoBands');
    const maxGroupNumber = visibleGroups.filter((item) => item?.numberofUnitsRequiredForComponent === maxSelectableItem());
    const featureFlags = useSelector(getFeatureFlags);
    const { isByoStaticProgressBarEnabled } = useFlags();

    return (
        <div
            className={style.mainContainer}
            data-testid="BYOProductItems"
            style={{
                '--btn-color-clear': buttonColorClear,
            }}
        >
            <div container className={style.innerProgressCartContainer}>
                {((!featureFlags['is-byo-mutiprogress-bar-enabled'] || maxGroupNumber?.length > 0) && !isByoStaticProgressBarEnabled) && (
                    <>
                        {device === 'desktop'
                            ? (
                                <ScrollbarContainer
                                    contentContainerId="progressBar"
                                    isFoodBrand
                                    type="productBase"
                                    style={style.progressBarSticky}
                                >
                                    <div id="progressBar" className={style.progressBar}>
                                        <ProgressBar
                                            totalProductSelected={totalProductSelected}
                                            maxSelectableForSku={maxSelectableForSku}
                                            removeItem={removeItem}
                                        />
                                    </div>
                                </ScrollbarContainer>
                            )
                            : (
                                <div ref={progressBarRef} className={isStickyHeader ? style.stickyProgressBar  : style.progressBar}>
                                    <ProgressBar
                                        totalProductSelected={totalProductSelected}
                                        maxSelectableForSku={maxSelectableForSku}
                                        isStickyHeader={isStickyHeader}
                                        removeItem={removeItem}
                                    />
                                </div>
                            )}
                    </>
                )}
                {visibleGroups.map((group) => {
                    const disableButton = maxGroupNumber.length > 0 ? totalProductSelected === maxSelectableForSku : selectedItems?.addItems?.[group.name]?.groupProductSelected >= group?.numberofUnitsRequiredForComponent;
                    return (
                        <BYOGroupSlider
                            groupData={group}
                            key={uid(group.name)}
                            selectedItems={selectedItems?.addItems?.[group.name] || {}}
                            handleItemSelection={handleItemSelection}
                            disabled={disableButton}
                            error={error}
                            handleRemoveError={handleRemoveError}
                            maxGroupNumber={maxGroupNumber}
                            selectedItem={selectedItems}
                            stepIndex={stepIndex}
                        />
                    );
                })}
            </div>
        </div>
    );
};
BYOProductItems.propTypes = {
    totalProductSelected: number,
    maxSelectableForSku: number,
    cyoData: shape({
        maxQuantity: number.isRequired,
        cyoGroup: arrayOf(
            shape({
                name: string.isRequired,
                components: arrayOf(
                    shape({
                        name: string.isRequired,
                        image: shape({
                            name: string.isRequired,
                            path: string.isRequired,
                        }),
                    }),
                ).isRequired,
            }),
        ).isRequired,
    }).isRequired,
    handleItemSelection: func.isRequired,
    selectedItems: object.isRequired,
    removeItem: func.isRequired,
    error: object.isRequired,
    handleRemoveError: func.isRequired,
    maxSelectableItem: func,
    stepIndex: string,
};
BYOProductItems.defaultProps = {
    maxSelectableForSku: 0,
    totalProductSelected: 0,
    maxSelectableItem: () => {},
    stepIndex: null,
};
export default BYOProductItems;
